import { type ReferentPriceType } from '@strata/constants';
import { sortAsc } from '@strata/helper-functions';
import { type DateRangeInputFormik } from '@strata/react-date-time';
import { type ComponentProps } from 'react';

import { type ConfiguredProductForPayer } from 'store/api/referenceApi';

/** Automation Rule Criteria Types */
export const AutomationRuleCriteria = {
    PAYER: 'Health Plan',
    PRODUCT: 'Product',
    PATIENT: 'Patient First Name',
    PERFORMANCE: 'Performance',
    PRESCRIBER_LIST: 'Prescriber List',
    STATE_LIST: 'State List',
    PLACE_OF_SERVICE: 'Place of Service',
} as const;

/** The different actions an automation rule can perform */
export const AutomationRuleActions = {
    SEND_REFERRAL: 'Send Referral',
    SET_MAX_BID: 'Set Max Bid',
} as const;

export const PerformanceCriteria = {
    NO_INC: 'NO_INC',
    NO_REQ: 'NO_REQ',
    REQ_INC: 'REQ_INC',
} as const;
/** The options for the performance criteria */
export const PerformanceCriteriaOptions = [
    { id: PerformanceCriteria.NO_INC, name: 'No Incentive' },
    { id: PerformanceCriteria.NO_REQ, name: 'No Requirements' },
    { id: PerformanceCriteria.REQ_INC, name: 'Requirements and Incentive' },
];

/** All payers id */
export const ALL_PAYERS = 'ALL_PAYERS';

export const ProductTypeOptions = [
    { name: 'Brand', value: true },
    { name: 'Generic', value: false },
] as const;

export const AutomationTemplateTypes = {
    PATIENT: 'PATIENT',
    PRODUCT: 'PRODUCT',
} as const;
export type AutomationTemplateType =
    (typeof AutomationTemplateTypes)[keyof typeof AutomationTemplateTypes];

export interface AutomationRule {
    id: string;
    isActive: boolean;
    template: AutomationTemplateType;
    payerId: string;
    segmentId: string;
    productServiceOid: string;
    memberFirstName?: string;
    memberId?: string;
    memberDOB?: string;
    performance?: (typeof PerformanceCriteria)[keyof typeof PerformanceCriteria];
    placeOfService?: string;
    prescriberListId?: string;
    stateListId?: string;
    action: {
        type?: (typeof AutomationRuleActions)[keyof typeof AutomationRuleActions];
        value?: { bidAmt?: number; referentPriceType?: ReferentPriceType; tempId?: string }[];
    };
    startDate?: string;
    endDate?: string;
    description: string;
    isBrand?: boolean;
}

interface AutomationRuleCriterionBase {
    criterion: (typeof AutomationRuleCriteria)[keyof typeof AutomationRuleCriteria];
    tempId?: string;
}
interface AutomationRuleCriterionPayer extends AutomationRuleCriterionBase {
    criterion: typeof AutomationRuleCriteria.PAYER;
    value: { id: string; payerId: string; name: string };
}
interface AutomationRuleCriterionProduct extends AutomationRuleCriterionBase {
    criterion: typeof AutomationRuleCriteria.PRODUCT;
    value: { oid: string; name: string; isBrand: boolean };
}
interface AutomationRuleCriterionPatient extends AutomationRuleCriterionBase {
    criterion: typeof AutomationRuleCriteria.PATIENT;
    value: { name: string; id: string; dob: string; benefitCheckSuccess: boolean };
}
interface AutomationRuleCriterionPerformance extends AutomationRuleCriterionBase {
    criterion: typeof AutomationRuleCriteria.PERFORMANCE;
    value: { id: (typeof PerformanceCriteria)[keyof typeof PerformanceCriteria]; name: string };
}
interface AutomationRuleCriterionPrescriberList extends AutomationRuleCriterionBase {
    criterion: typeof AutomationRuleCriteria.PRESCRIBER_LIST;
    value: { prescriberListId: string; name: string };
}
interface AutomationRuleCriterionStateList extends AutomationRuleCriterionBase {
    criterion: typeof AutomationRuleCriteria.STATE_LIST;
    value: { stateListId: string; name: string };
}
interface AutomationRuleCriterionPlaceOfService extends AutomationRuleCriterionBase {
    criterion: typeof AutomationRuleCriteria.PLACE_OF_SERVICE;
    value: { id: string; name: string };
}
interface AutomationRuleCriterionMap {
    [AutomationRuleCriteria.PAYER]: AutomationRuleCriterionPayer;
    [AutomationRuleCriteria.PRODUCT]: AutomationRuleCriterionProduct;
    [AutomationRuleCriteria.PATIENT]: AutomationRuleCriterionPatient;
    [AutomationRuleCriteria.PERFORMANCE]: AutomationRuleCriterionPerformance;
    [AutomationRuleCriteria.PRESCRIBER_LIST]: AutomationRuleCriterionPrescriberList;
    [AutomationRuleCriteria.STATE_LIST]: AutomationRuleCriterionStateList;
    [AutomationRuleCriteria.PLACE_OF_SERVICE]: AutomationRuleCriterionPlaceOfService;
}
export type AutomationRuleCriterion<
    T extends (typeof AutomationRuleCriteria)[keyof typeof AutomationRuleCriteria],
> = AutomationRuleCriterionMap[T];

export interface AutomationRuleFormContext {
    criteria: AutomationRuleCriterionMap[keyof AutomationRuleCriterionMap][];
    action: {
        type?: (typeof AutomationRuleActions)[keyof typeof AutomationRuleActions];
        value?: { bidAmt: number; referentPriceType: ReferentPriceType; tempId?: string }[];
        terms?: string[];
    };
    dateRange?: ComponentProps<typeof DateRangeInputFormik>['value'];
    description: string;
}

export const findAutomationRuleCriterionValue = <
    T extends (typeof AutomationRuleCriteria)[keyof typeof AutomationRuleCriteria],
>(
    criteria?: AutomationRuleCriterionMap[keyof AutomationRuleCriterionMap][],
    criterionToFind?: T,
): AutomationRuleCriterion<T>['value'] | undefined =>
    criterionToFind
        ? criteria?.find(({ criterion }) => criterion === criterionToFind)?.value
        : undefined;

export const findAutomationRuleCriterionIndex = (
    criteria?: {
        criterion: (typeof AutomationRuleCriteria)[keyof typeof AutomationRuleCriteria];
    }[],
    criterionToFind?: (typeof AutomationRuleCriteria)[keyof typeof AutomationRuleCriteria],
) =>
    criterionToFind
        ? criteria?.findIndex(({ criterion }) => criterion === criterionToFind)
        : undefined;

export const mapProductsToBrandAndGenericProducts = (
    configuredProductsList?: ConfiguredProductForPayer[],
) => {
    const products: { oid: string; name: string; secondaryData: string; isBrand: boolean }[] = [];

    configuredProductsList?.forEach((product) => {
        products.push({
            oid: product.oid,
            name: product.brandProductDescription,
            secondaryData: 'Brand',
            isBrand: true,
        });

        if (
            product.genericProductDescription &&
            product.brandProductDescription?.toLowerCase() !==
                product.genericProductDescription?.toLowerCase()
        ) {
            products.push({
                oid: product.oid,
                name: product.genericProductDescription,
                secondaryData: 'Generic',
                isBrand: false,
            });
        }
    });
    // Filter out duplicate names when multiple policies are configured
    return [...new Map(products?.map((product) => [product.name, product])).values()].sort((a, b) =>
        sortAsc(a.name, b.name),
    );
};
